import type { ProductDetail, ProductRow, ProductStatus } from "./products";
import { VariantRow, generateVariantId } from "@shared/lib/variantMatrix";
import { coerceMoney, resolveDisplayPricing } from "@shared/lib/productPricing";

export function ensureId(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function sanitizeString(str: string | undefined | null): string {
  if (!str) return "";
  return str
    .replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gim, "")
    .replace(/javascript:/gim, "")
    .replace(/ on\w+="[^"]*"/gim, "")
    .trim();
}

export function isValidGuid(id: string | undefined | null): boolean {
  if (!id) return false;
  const guidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidPattern.test(id);
}

export function normalizeVariant(raw: any): VariantRow {
  const rawOptions =
    raw.options ?? raw.Options ?? raw.optionValues ?? raw.OptionValues ?? {};
  let options: Record<string, string> = {};
  if (Array.isArray(rawOptions)) {
    rawOptions.forEach((opt: any) => {
      const name = opt?.name ?? opt?.Name;
      const value = opt?.value ?? opt?.Value;
      if (name) options[name] = value ?? "";
    });
  } else if (rawOptions && typeof rawOptions === "object") {
    options = rawOptions;
  }
  return {
    id: raw.id ?? raw.Id ?? generateVariantId(options),
    options,
    sku: raw.sku ?? raw.SKU ?? "",
    barcode: raw.barcode ?? raw.Barcode ?? "",
    price: Number(raw.price ?? raw.Price ?? 0),
    inheritsBasePrice: raw.inheritsBasePrice ?? raw.InheritsBasePrice ?? false,
    compareAt:
      coerceMoney(
        raw.salePrice ??
          raw.SalePrice ??
          raw.sale_price ??
          raw.Sale_Price ??
          raw.compareAt ??
          raw.CompareAt ??
          raw.compare_at ??
          raw.compareAtPrice ??
          raw.CompareAtPrice,
      ) ?? null,
    cost: typeof raw.cost === "number" ? raw.cost : (raw.Cost ?? null),
    weight: typeof raw.weight === "number" ? raw.weight : (raw.Weight ?? null),
    stockQty: Number(
      raw.stockQty ??
        raw.stockQuantity ??
        raw.StockQuantity ??
        raw.stock ??
        raw.Stock ??
        0,
    ),
    stockQuantity: Number(
      raw.stockQty ??
        raw.stockQuantity ??
        raw.StockQuantity ??
        raw.stock ??
        raw.Stock ??
        0,
    ),
    isActive: raw.isActive ?? raw.IsActive ?? true,
    currency: raw.currency ?? raw.Currency,
    trackInventory: raw.trackInventory ?? raw.TrackInventory,
    isDefault: raw.isDefault ?? raw.IsDefault,
    tierPrices: raw.tierPrices ?? raw.TierPrices ?? {},
    sortOrder: Number(raw.sortOrder ?? raw.SortOrder ?? 0),
  };
}

export function mapVariantOptions(options: Record<string, any> | undefined) {
  if (!options) return undefined;
  const entries = Object.entries(options).filter(([name]) => !!name);
  if (!entries.length) return [];
  return entries.map(([name, value]) => ({
    Name: name,
    Value:
      typeof value === "object" && value !== null && "value" in (value as any)
        ? (value as any).value
        : String(value ?? ""),
  }));
}

function normalizeOptionValue(value: unknown): string {
  if (value && typeof value === "object" && "value" in (value as any)) {
    return String((value as any).value ?? "");
  }
  return String(value ?? "");
}

export function hasVariantOptionValues(
  variant?: Pick<VariantRow, "options"> | null,
): boolean {
  return Object.entries(variant?.options || {}).some(
    ([name, value]) =>
      String(name ?? "").trim().length > 0 &&
      normalizeOptionValue(value).trim().length > 0,
  );
}

function getVariantRowsForRollup(product: ProductDetail): VariantRow[] {
  const variants = product.variants || [];
  const actualRows = variants.filter(hasVariantOptionValues);
  if (product.productType === 1) return actualRows;
  return actualRows.length > 0 ? actualRows : variants;
}

export function mapImageToApi(image: any, index: number) {
  const sortOrder =
    typeof image?.sortOrder === "number"
      ? image.sortOrder
      : typeof image?.SortOrder === "number"
        ? image.SortOrder
        : typeof image?.sort === "number"
          ? image.sort
          : index;

  return {
    Id: image?.id ?? image?.Id ?? null,
    Url: image?.url ?? image?.Url ?? "",
    AltText: image?.alt ?? image?.altText ?? image?.AltText ?? "",
    IsPrimary: Boolean(image?.isPrimary ?? image?.IsPrimary),
    SortOrder: sortOrder,
  };
}

export function normalizeProductStatusValue(
  value: unknown,
): ProductStatus | null {
  if (typeof value !== "string") return null;

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[,\u2013\u2014]/g, "")
    .replace(/[\s-]+/g, "_");
  if (!normalized) return null;

  if (normalized === "active") return "active";
  if (normalized === "inactive") return "inactive";

  return null;
}

export function normalizeProductStatus(raw: any): ProductStatus {
  const explicitStatus = normalizeProductStatusValue(raw.status ?? raw.Status);
  if (explicitStatus) return explicitStatus;

  return (raw.isActive ?? raw.IsActive ?? true) ? "active" : "inactive";
}

export function isActiveProductStatus(
  status?: ProductStatus | string | null,
): boolean {
  const normalized = normalizeProductStatusValue(status);
  return normalized === "active";
}

export function getProductStatusLabel(
  status?: ProductStatus | string | null,
): string {
  return (normalizeProductStatusValue(status) ?? "active") === "active"
    ? "Active"
    : "Inactive";
}

export function matchesProductStatusFilter(
  status: ProductStatus,
  filter?: ProductStatus | "",
): boolean {
  if (!filter) return true;
  return status === filter;
}

export function normalizeProduct(raw: any): ProductDetail {
  const rawVariants = Array.isArray(raw.variants)
    ? raw.variants
    : Array.isArray(raw.Variants)
      ? raw.Variants
      : [];
  const variants = rawVariants.map(normalizeVariant).sort((a, b) => {
    const orderDelta = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (orderDelta !== 0) return orderDelta;
    const aSignature = generateVariantId(a.options);
    const bSignature = generateVariantId(b.options);
    return (a.sku || aSignature || a.id).localeCompare(
      b.sku || bSignature || b.id,
    );
  });
  const apiPriceMin = coerceMoney(raw.priceMin ?? raw.PriceMin) ?? null;
  const apiPriceMax = coerceMoney(raw.priceMax ?? raw.PriceMax) ?? null;
  const rawPrice = coerceMoney(raw.price ?? raw.Price);
  const rawSalePrice = coerceMoney(
    raw.salePrice ?? raw.SalePrice ?? raw.sale_price ?? raw.Sale_Price,
  );
  const rawCompareAtPrice = coerceMoney(
    raw.compareAtPrice ??
      raw.CompareAtPrice ??
      raw.compareAt ??
      raw.CompareAt ??
      raw.compareAtMax ??
      raw.CompareAtMax,
  );
  const inferredSalePrice =
    rawSalePrice ??
    (rawCompareAtPrice !== undefined &&
    apiPriceMin !== null &&
    rawCompareAtPrice > apiPriceMin
      ? apiPriceMin
      : undefined);
  const regularPrice =
    rawPrice ??
    (inferredSalePrice !== undefined &&
    rawCompareAtPrice !== undefined &&
    inferredSalePrice <= rawCompareAtPrice
      ? rawCompareAtPrice
      : undefined) ??
    apiPriceMax ??
    apiPriceMin ??
    0;
  const displayPricing = resolveDisplayPricing(
    regularPrice,
    inferredSalePrice,
    rawCompareAtPrice,
    apiPriceMin ?? regularPrice,
  );
  const salePrice = displayPricing.salePrice ?? null;
  const price = regularPrice;
  const rawCategoryIds = raw.categoryIds ?? raw.CategoryIds ?? raw.category_ids;
  const normalizedCategoryIds = Array.isArray(rawCategoryIds)
    ? rawCategoryIds
    : [];
  const fallbackCategoryId =
    raw.categoryId ?? raw.CategoryId ?? raw.category_id;
  const categoryIds =
    normalizedCategoryIds.length > 0
      ? normalizedCategoryIds
      : fallbackCategoryId
        ? [fallbackCategoryId]
        : [];
  const rawLabelIds = raw.labelIds ?? raw.LabelIds ?? raw.label_ids;
  const rawLabels = raw.labels ?? raw.Labels;
  const labelIds = Array.isArray(rawLabelIds)
    ? rawLabelIds.map((value: any) => String(value))
    : Array.isArray(rawLabels)
      ? rawLabels
          .map((label: any) => label?.id ?? label?.Id ?? label)
          .filter(Boolean)
          .map((value: any) => String(value))
      : [];
  const rawImages = Array.isArray(raw.images)
    ? raw.images
    : Array.isArray(raw.Images)
      ? raw.Images
      : [];
  const rawImageUrlValue = raw.imageUrl ?? raw.ImageUrl ?? raw.image_url;
  const rawImageUrl =
    typeof rawImageUrlValue === "string" && rawImageUrlValue.trim()
      ? rawImageUrlValue
      : undefined;
  const normalizedImages = rawImages.map((img: any, idx: number) => ({
    id: img.id ?? img.Id ?? ensureId("img"),
    url: img.url ?? img.Url ?? "",
    alt: img.alt ?? img.altText ?? img.AltText ?? "",
    sort:
      typeof img.sort === "number"
        ? img.sort
        : typeof img.sortOrder === "number"
          ? img.sortOrder
          : typeof img.SortOrder === "number"
            ? img.SortOrder
            : idx,
    isPrimary: img.isPrimary ?? img.IsPrimary ?? idx === 0,
  }));

  if (normalizedImages.length === 0 && rawImageUrl) {
    normalizedImages.push({
      id: ensureId("img"),
      url: rawImageUrl,
      alt: "",
      sort: 0,
      isPrimary: true,
    });
  }

  const base: ProductDetail = {
    id: String(raw.id ?? raw.Id ?? ensureId("prod")),
    name: raw.name ?? raw.title ?? raw.Name ?? "Untitled product",
    description: raw.description ?? "",
    shortDescription: raw.shortDescription ?? raw.ShortDescription ?? "",
    slug: raw.slug ?? raw.Slug ?? "",
    rowVersion: raw.rowVersion ?? raw.RowVersion ?? null,
    sku: raw.sku ?? raw.SKU ?? "",
    price,
    salePrice,
    currency:
      raw.currency ?? raw.defaultCurrency ?? raw.DefaultCurrency ?? "USD",
    status: normalizeProductStatus(raw),
    activateAtUtc: raw.activateAtUtc ?? raw.ActivateAtUtc ?? null,
    storefrontSortOrder: Number(
      raw.storefrontSortOrder ?? raw.StorefrontSortOrder ?? 0,
    ),
    createdAt:
      raw.createdAt ??
      raw.createdAtUtc ??
      raw.CreatedAt ??
      raw.CreatedAtUtc ??
      new Date().toISOString(),
    updatedAt:
      raw.updatedAt ??
      raw.updatedAtUtc ??
      raw.UpdatedAt ??
      raw.UpdatedAtUtc ??
      new Date().toISOString(),
    trackInventory: raw.trackInventory ?? raw.TrackInventory ?? true,
    stockQty: Number(
      raw.stockQty ??
        raw.stockQuantity ??
        raw.StockQuantity ??
        raw.StockQty ??
        0,
    ),
    brandId: raw.brandId ?? raw.BrandId ?? raw.brand_id ?? null,
    brandName: raw.brandName ?? raw.BrandName ?? null,
    brandSlug: raw.brandSlug ?? raw.BrandSlug ?? null,
    categoryIds,
    labelIds,
    translations: raw.translations ?? raw.Translations ?? {},
    metaTitle: raw.metaTitle ?? raw.MetaTitle,
    metaDescription: raw.metaDescription ?? raw.MetaDescription,
    imageUrl: rawImageUrl,
    images: normalizedImages,
    variants,
    priceMin: 0,
    priceMax: 0,
    compareAt: displayPricing.compareAtPrice ?? null,
    publicUrl: raw.publicUrl ?? raw.PublicUrl ?? "",
    productType: raw.productType ?? raw.ProductType ?? 0,
    categoryCount: raw.categoryCount ?? raw.CategoryCount ?? 0,
    weight: typeof raw.weight === "number" ? raw.weight : (raw.Weight ?? null),
  };
  if (!variants.length && Number.isFinite(apiPriceMin)) {
    base.priceMin = Number(apiPriceMin);
    base.priceMax = Number.isFinite(apiPriceMax)
      ? Number(apiPriceMax)
      : Number(apiPriceMin);
  } else {
    const range = computeRange(base);
    base.priceMin = range.min;
    base.priceMax = range.max;
  }
  base.stockQty = computeStock(base);
  return base;
}

export function computeRange(product: ProductDetail): {
  min: number;
  max: number;
} {
  const basePrice = Number(product.salePrice ?? product.price ?? 0) || 0;
  const variantPrices = getVariantRowsForRollup(product)
    .filter((v) => v.isActive !== false)
    .map((v) => (v.inheritsBasePrice ? basePrice : Number(v.price ?? 0)))
    .filter((n) => Number.isFinite(n));
  if (variantPrices.length) {
    const min = Math.min(...variantPrices);
    const max = Math.max(...variantPrices);
    return { min, max };
  }
  return {
    min: basePrice,
    max: Number(product.price ?? basePrice) || basePrice,
  };
}

export function computeStock(product: ProductDetail): number {
  const actualVariants = getVariantRowsForRollup(product);
  if (actualVariants.length) {
    return actualVariants
      .filter((v) => v.isActive !== false)
      .reduce((sum, v) => sum + (Number(v.stockQty) || 0), 0);
  }
  if (product.productType === 1) return 0;
  return Number(product.stockQty ?? 0);
}

export function toRow(product: ProductDetail): ProductRow {
  return {
    id: product.id,
    name: product.name,
    price: product.price,
    priceMin: Number(product.priceMin ?? 0) || 0,
    priceMax: Number(product.priceMax ?? product.priceMin ?? 0) || 0,
    status: product.status,
    activateAtUtc: product.activateAtUtc,
    storefrontSortOrder: product.storefrontSortOrder,
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
    imageUrl:
      product.images.find((i) => i.isPrimary)?.url ??
      product.images[0]?.url ??
      product.imageUrl ??
      (product as ProductDetail & { ImageUrl?: string }).ImageUrl ??
      undefined,
    slug: product.slug,
    salePrice: product.salePrice ?? null,
    compareAt: product.compareAt ?? product.salePrice ?? null,
    compareAtPrice: product.compareAt ?? null,
    compareAtMax: product.compareAt ?? null,
    stockQty: computeStock(product),
    trackInventory: product.trackInventory,
    currency: product.currency,
    brandId: product.brandId,
    brandName: product.brandName,
    brandSlug: product.brandSlug,
    categoryIds: product.categoryIds,
    sku: product.sku,
    publicUrl: product.publicUrl,
    categoryCount: product.categoryCount,
    weight: product.weight,
  };
}

export function parseDownloadFileName(
  contentDisposition: string | null,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  const simple = /filename="?([^";]+)"?/i.exec(contentDisposition)?.[1];
  return simple || fallback;
}
